/**
 * DeckBuilder.tsx — basic drag-drop deck construction. Filter cards by
 * orbital / cost / type, drag into the deck panel, name + save.
 *
 * Phase 1 ships a minimum-viable builder. Validation is permissive
 * (no orbital reqs, no count enforcement) — sandbox first.
 */

import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useCards } from '../hooks/useCards.js';
import { useDecks } from '../hooks/useDecks.js';
import type { CardData, RealmName } from '../types.js';
import { REALMS } from '../types.js';

type Props = {
  onSaved: (deckId: string) => void;
  onCancel: () => void;
  /** Pre-populate the builder with an existing deck's contents (clone-and-edit
   *  flow). When set, the name input shows `${seedDeck.name}` verbatim — the
   *  caller is expected to supply the " (copy)" suffix and pre-cloned cards. */
  seedDeck?: {
    name: string;
    cardIds: readonly string[];
    sourceName?: string;
  };
};

const TYPE_OPTIONS: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Any', value: '' },
  { label: 'Wonder', value: 'Wonder' },
  { label: 'Land', value: 'Land' },
  { label: 'Spell', value: 'Spell' },
  { label: 'Item', value: 'Item' },
];

export function DeckBuilder({ onSaved, onCancel, seedDeck }: Props) {
  const cards = useCards();
  const decks = useDecks();

  const [name, setName] = useState(seedDeck?.name ?? 'My deck');
  const [picked, setPicked] = useState<string[]>(seedDeck ? Array.from(seedDeck.cardIds) : []);
  const [search, setSearch] = useState('');
  const [orbitalFilter, setOrbitalFilter] = useState<RealmName | ''>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Apply filters once on the cached card map. Sort by name.
  const filtered = useMemo<CardData[]>(() => {
    if (cards.status !== 'ready') return [];
    const all = Array.from(cards.cards.values());
    const q = search.trim().toLowerCase();
    return all
      .filter((c) => (q ? c.name.toLowerCase().includes(q) : true))
      .filter((c) => {
        if (!orbitalFilter) return true;
        const text = `${c.raw_text} ${c.name}`.toLowerCase();
        return text.includes(orbitalFilter.toLowerCase());
      })
      .filter((c) => {
        if (!typeFilter) return true;
        // Card type isn't in the API response — infer from raw_text or
        // fall back to "all". Tightened in Phase 3.
        const text = c.raw_text.toLowerCase();
        return text.includes(typeFilter.toLowerCase()) || true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [cards, search, orbitalFilter, typeFilter]);

  const addCard = (cardId: string) => setPicked((p) => [...p, cardId]);
  const removeAt = (idx: number) => setPicked((p) => p.filter((_, i) => i !== idx));

  const save = async () => {
    if (!name.trim() || picked.length === 0) return;
    setSaving(true);
    const id = await decks.create(name.trim(), picked, 'private');
    setSaving(false);
    if (id) onSaved(id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch bg-cosmos-950/90 backdrop-blur p-4">
      <div className="bg-cosmos-900 ring-1 ring-cosmos-700 rounded-tile flex-1 flex overflow-hidden">
        {/* Left: card browser */}
        <section className="flex-[2] border-r border-cosmos-700 flex flex-col p-3 overflow-hidden">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="font-display text-xl">Card library</h3>
            {cards.status === 'ready' && (
              <span className="text-xs text-cosmos-400">
                {filtered.length} of {cards.cards.size}
              </span>
            )}
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            className="bg-cosmos-700 px-3 py-1.5 rounded text-sm placeholder-cosmos-400 mb-2"
          />
          <div className="flex gap-2 mb-2">
            <select
              value={orbitalFilter}
              onChange={(e) => setOrbitalFilter(e.target.value as RealmName | '')}
              className="bg-cosmos-700 px-2 py-1 rounded text-sm flex-1"
            >
              <option value="">All orbitals</option>
              {REALMS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-cosmos-700 px-2 py-1 rounded text-sm flex-1"
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <ul className="flex-1 overflow-y-auto space-y-0.5">
            {filtered.slice(0, 200).map((c) => (
              <li key={c.card_id}>
                <button
                  type="button"
                  onClick={() => addCard(c.card_id)}
                  className="w-full text-left px-2 py-1 rounded hover:bg-cosmos-700 text-sm flex items-center gap-2"
                >
                  <img
                    src={cards.imageUrl(c.card_id)}
                    alt=""
                    loading="lazy"
                    className="w-8 h-11 rounded object-cover"
                  />
                  <span className="flex-1">{c.name}</span>
                  <span className="text-xs text-cosmos-400">{c.card_id}</span>
                </button>
              </li>
            ))}
            {filtered.length > 200 && (
              <li className="text-xs text-cosmos-400 italic px-2 py-1">
                … {filtered.length - 200} more (refine search to see them)
              </li>
            )}
          </ul>
        </section>

        {/* Right: deck panel */}
        <section className="flex-1 flex flex-col p-3 overflow-hidden">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl">Your deck</h3>
            <span className="text-xs text-cosmos-400">{picked.length} cards</span>
          </div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Deck name"
            className="bg-cosmos-700 px-3 py-1.5 rounded text-sm placeholder-cosmos-400 mb-2"
          />
          {seedDeck?.sourceName ? (
            <p className="-mt-1 mb-2 text-[11px] italic text-cosmos-400">
              cloned from <span className="font-mono">{seedDeck.sourceName}</span>
            </p>
          ) : null}
          <ul className="flex-1 overflow-y-auto space-y-0.5">
            {picked.length === 0 && (
              <li className="text-xs text-cosmos-400 italic px-2 py-1">
                Click a card on the left to add.
              </li>
            )}
            {picked.map((cardId, idx) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: deck may contain duplicate card_ids; index disambiguates within the picked list.
                key={`${cardId}-${idx}`}
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-cosmos-700 text-sm"
              >
                <span className="flex-1">{cards.cards.get(cardId)?.name ?? cardId}</span>
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="text-cosmos-400 hover:text-rose-400 text-xs"
                  aria-label="Remove"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-2 rounded bg-cosmos-700 hover:bg-cosmos-400/30 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !name.trim() || picked.length === 0}
              className={clsx(
                'flex-1 py-2 rounded text-sm font-bold transition',
                saving || !name.trim() || picked.length === 0
                  ? 'bg-cosmos-700 text-cosmos-400 cursor-not-allowed'
                  : 'bg-emerald-600 text-cosmos-50 hover:bg-emerald-500',
              )}
            >
              {saving ? 'Saving…' : 'Save deck'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
